import * as React from 'react';
import PharmagardeMap from '../common/map/PharmagardeMap';
import { Circle, GoogleMap } from '@react-google-maps/api';
import { getUserLocation } from '../../store/utils';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { Instance } from 'mobx-state-tree';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import { Button, Checkbox, Label, Spinner } from '@blueprintjs/core';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { TLCode } from '../../common/enums';
import DateInput from '../common/input/DateInput';
import moment from 'moment';
import PharmacyMarker from '../common/PharmacyMarker';
import { IconNames } from '@blueprintjs/icons';
import PharmacySelection from '../common/dropdownSelection/PharmacySelection';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Anchor } from '../../store/models/Anchor';

import NotificationToast from '../common/NotificationToast';
import { generateBasicIcon } from '../../common/icons';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MapContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0.495;
`;

const Maps = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const DivFlex = styled.div`
  display: flex;
  flex: 1;
`;

interface ShowDutiesOnMapProps extends WithTranslateProps {
  searchDuties: (form: any) => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  getPharmacyById: (id: number) => any;
  getPharmacyDetails: (id: number) => any;
  date?: string;
  anchors: Array<Instance<typeof Anchor>>;
  dutySelected?: Instance<typeof DutyAssignment>;
  onDateChange: (date: string) => void;
  isSecretariat?: boolean;
  presetPharmacy?: Instance<typeof PharmacyDetails>;
}

interface ShowDutiesOnMapState {
  userLocation?: GeolocationPosition;
  requestingUserLocation: boolean;
  loading: boolean;
  initialZoomCalculatedDay: boolean;
  initialZoomCalculatedNight: boolean;
  showPharmacyNotInDuty: boolean;
  showPharmacyDutyCircle: boolean;
  showPharmacyDuty30kmCircle: boolean;
  showAnchor: boolean;
  searchDate: Date;
  searchPlanning: string;
  dutiesDay: Array<Instance<typeof DutyAssignment>>;
  dutiesNight: Array<Instance<typeof DutyAssignment>>;
  pharmacySelected?: Instance<typeof PharmacyDetails>;
}

class ShowDutiesOnMap extends React.Component<ShowDutiesOnMapProps, ShowDutiesOnMapState> {
  state = {
    requestingUserLocation: false,
    initialZoomCalculatedDay: false,
    initialZoomCalculatedNight: false,
    loading: false,
    showPharmacyNotInDuty: false,
    showPharmacyDutyCircle: false,
    showPharmacyDuty30kmCircle: false,
    showAnchor: false,
    searchDate: new Date(this.props.date!), // new Date(),
    searchPlanning: '',
    dutiesDay: [],
    dutiesNight: [],
    pharmacyList: [],
  } as ShowDutiesOnMapState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    try {
      const location = await getUserLocation()
        .then((position) => {
          this._isMounted && this.setState({ requestingUserLocation: false, userLocation: position });
        })
        .catch((err) => {
          const uphocPosition = {
            coords: {
              latitude: 50.41753,
              longitude: 3.908192,
              accuracy: 20,
              altitude: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null,
            },
            timestamp: new Date().getTime(),
          };
          this._isMounted && this.setState({ requestingUserLocation: false, userLocation: uphocPosition });
        });

      this._isMounted && this.setState({ pharmacySelected: this.props.presetPharmacy });
      let date = moment(this.state.searchDate).format();
      if (this.props.dutySelected != null) {
        const pharmacy = this.props.pharmacyList.find(
          (elem) => elem.pharmacyId === this.props.dutySelected!.pharmacyId,
        );
        const pharmacyDetails = await this.props.getPharmacyDetails(pharmacy!.pharmacyId);
        this._isMounted &&
          this.setState({
            searchDate: new Date(this.props.dutySelected!.duty!.dutyDate!.date),
            pharmacySelected: pharmacyDetails,
          });
        date = moment(this.props.dutySelected!.duty!.dutyDate!.date).format();
      } else if (this.props.date != null) {
        this._isMounted && this.setState({ searchDate: new Date(this.props.date) });
        date = this.props.date;
      }
      this.searchDuties({ date, notAssigned: false });
    } catch (err) {
      console.error(`Error while getting user location: ${(err as any).message}`);
      this._isMounted && this.setState({ requestingUserLocation: false });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  searchDuties = async (date) => {
    const result: any = await this.props.searchDuties(date);
    const dutiesDay = result.filter((elem) => elem.duty.dutyType.type === 'DAY');
    const dutiesNight = result.filter((elem) => elem.duty.dutyType.type === 'NIGHT');
    this._isMounted && this.setState({ dutiesDay, dutiesNight });
    // this.fitBoundsDay();
    // this.fitBoundsNight();
  };

  selectPharmacy = async (pharmacy) => {
    await this.onPharmacyChange(pharmacy);
  };

  findCenterFromCoordinateList(listCoordinate) {
    let top;
    let bottom;
    let left;
    let right;
    listCoordinate.forEach((point) => {
      if (!top || point.lat > top) {
        top = point.lat;
      }
      if (!bottom || point.lat < bottom) {
        bottom = point.lat;
      }
      if (!left || point.lng < left) {
        left = point.lng;
      }
      if (!right || point.lng > right) {
        right = point.lng;
      }
    });
    const lat = (top + bottom) / 2;
    const lng = (left + right) / 2;

    return {
      lat,
      lng,
    };
  }

  findCenterCoordinate = (type: string) => {
    const { pharmacySelected, showPharmacyNotInDuty, dutiesNight, dutiesDay, userLocation } = this.state;
    const { pharmacyList } = this.props;
    if (pharmacySelected != null && pharmacySelected.latitude && pharmacySelected.longitude) {
      return { lat: pharmacySelected.latitude, lng: pharmacySelected.longitude };
    } else if (showPharmacyNotInDuty && pharmacyList.filter((ph) => ph.latitude && ph.longitude).length !== 0) {
      return this.findCenterFromCoordinateList(
        pharmacyList
          .filter((ph) => ph.latitude && ph.longitude)
          .map((ph) => {
            return { lat: ph.latitude, lng: ph.longitude };
          }),
      );
    } else if (type === 'DAY' && dutiesDay.length !== 0) {
      return this.findCenterFromCoordinateList(
        dutiesDay
          .filter((d) => d !== undefined && d.pharmacySimple && d.pharmacySimple.latitude && d.pharmacySimple.longitude)
          .map((d) => {
            return { lat: d.pharmacySimple!.latitude, lng: d.pharmacySimple!.longitude };
          }),
      );
    } else if (type === 'NIGHT' && dutiesNight.length !== 0) {
      return this.findCenterFromCoordinateList(
        dutiesNight
          .filter((d) => d !== undefined && d.pharmacySimple && d.pharmacySimple.latitude && d.pharmacySimple.longitude)
          .map((d) => {
            return { lat: d.pharmacySimple!.latitude, lng: d.pharmacySimple!.longitude };
          }),
      );
    } else if (userLocation) {
      return {
        lat: userLocation.coords.latitude,
        lng: userLocation.coords.longitude,
      };
    } else {
      return undefined;
    }
  };

  renderPharmacyDutiesMarkers = (duties: Array<Instance<typeof DutyAssignment>>, type: string) => {
    const { pharmacySelected } = this.state;
    const markers: any[] = [];

    duties
      // .filter((d) => d.pharmacy && d.pharmacy.latitude && d.pharmacy.longitude)
      .filter((d) => d.pharmacyId)
      .forEach((d, index) => {
        const pharmacies = this.props.pharmacyList.filter((ph) => ph.pharmacyId == d.pharmacyId!);
        if (pharmacies.length == 0) {
          //might happen if one of the pharmacies with a duty that day is not active (error in data)
          console.log('error');
          NotificationToast.showError(
            'Data error: pharmacy [' + d.pharmacyId + '] could not be displayed (check active flag?)',
          );
        } else {
          //npm install @types/google-maps --save
          //const svgIcon = {url: locationPin,anchor: new window.google.maps.Point(5, 58),scaledSize: new window.google.maps.Size(64,64)}
          //const svgIcon = {url: locationPin}
          /*
          const svgIcon = {
            url: generateLocationSvgIcon(32,32,'225577','888888',false,'AACC00',true,'AACCFF')
          }
          */
          let icon = generateBasicIcon(pharmacies[0]!.level, false, false, false, false);
          // d.pharmacy!.level === 3
          //   ? yellowDot
          //   : (d.pharmacy!.level === 1
          //     ? yellow
          //     : mm20Gray);
          // pharmacies[0]!.level === 3 ? yellowDot : pharmacies[0]!.level === 1 ? yellow : mm20Gray;

          if (pharmacySelected != null) {
            const neighborhood = pharmacySelected!.pharmacyNeighbors.find((elem) => {
              return elem.neighborId === d.pharmacyId;
            });
            if (pharmacySelected.pharmacyId === d.pharmacyId) {
              icon = generateBasicIcon(pharmacies[0]!.level, false, true, false, false);
              /*
              if (pharmacySelected.level === 3) {
                icon = blueDot; //icon11;
              } else {
                icon = blue; //icon11;
              }
              */
              // if (pharmacySelected.level != null && pharmacySelected.level === 3) {
              //   icon = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
              // }
            } else if (neighborhood !== undefined && neighborhood!.distance != null) {
              const neighbor = this.props.pharmacyList.find((ph) => ph.pharmacyId === neighborhood.neighborId);
              if (type === 'DAY' && pharmacySelected!.minDistance != null && pharmacySelected!.maxDistance != null) {
                if (neighborhood!.distance < pharmacySelected!.minDistance) {
                  icon = generateBasicIcon(neighbor!.level, false, false, false, true);
                }
              } else if (
                type === 'NIGHT' &&
                pharmacySelected!.minDistance2 != null &&
                pharmacySelected!.maxDistance2 != null
              ) {
                if (neighborhood!.distance < pharmacySelected!.minDistance2) {
                  icon = generateBasicIcon(neighbor!.level, false, false, false, true);
                }
              }
            }
          }
          markers.push(
            <PharmacyMarker
              key={d.dutyAssignmentId}
              id={d.dutyAssignmentId}
              // marker={d.pharmacy!}
              marker={pharmacies[0]!}
              zIndex={100}
              icon={icon}
              onSelectionClick={this.selectPharmacy}
            />,
          );
        }
        if (this.state.showPharmacyDuty30kmCircle && type === 'DAY') {
          markers.push(
            <Circle
              key={d.pharmacySimple!.pharmacyId + '15km'}
              center={{ lat: Number(d.pharmacySimple!.latitude), lng: Number(d.pharmacySimple!.longitude) }}
              radius={15000}
              options={{
                fillColor: '#A9CCE3',
                fillOpacity: 0.1,
                strokeWeight: 0.5,
              }}
            />,
          );
        }
        if (this.state.showPharmacyDuty30kmCircle && type === 'NIGHT') {
          markers.push(
            <Circle
              key={d.pharmacySimple!.pharmacyId + '20km'}
              center={{ lat: Number(d.pharmacySimple!.latitude), lng: Number(d.pharmacySimple!.longitude) }}
              radius={20000}
              options={{
                fillColor: '#A9CCE3',
                fillOpacity: 0.1,
                strokeWeight: 0.5,
              }}
            />,
          );
        }
        if (this.state.showPharmacyDutyCircle) {
          markers.push(...this.renderCircleForPharmacy(d.pharmacyDetails, type));
        }
      });
    return markers;
  };

  renderPharmacyMarkers = (duties: Array<Instance<typeof DutyAssignment>>, type: string) => {
    //console.log("renderPharmacyMarkers_START"+new Date())
    const { pharmacySelected, showPharmacyNotInDuty } = this.state;
    const pharmacyNotInGarde = this.props.pharmacyList.filter((elem) => {
      return duties.find((d) => d.duty!.dutyType!.type === type && d.pharmacyId === elem.pharmacyId) === undefined;
    });
    const pharmacySelectedNotInGarde =
      pharmacySelected &&
      pharmacyNotInGarde.find((elem) => elem.pharmacyId === pharmacySelected.pharmacyId) !== undefined;
    const result = pharmacyNotInGarde
      .filter(
        (ph) =>
          ph.latitude &&
          ph.longitude &&
          (showPharmacyNotInDuty || (pharmacySelectedNotInGarde && ph.pharmacyId === pharmacySelected!.pharmacyId)),
      )
      .map((ph) => {
        let icon = generateBasicIcon(0, false, false, false, false);
        //let icon = mm20Gray;
        if (!ph.isActive) {
          alert('test');
        }
        if (pharmacySelected != null && pharmacySelected.pharmacyId === ph.pharmacyId) {
          icon = generateBasicIcon(pharmacySelected.level, false, false, true, false);
          /*
          if (pharmacySelected.level === 3) {
            icon = lightblueDot; // icon3;
          } else {
            icon = lightblue;
          }
          */
          // if (pharmacySelected.level != null && pharmacySelected.level === 3) {
          //   icon = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
          // }
        }
        return (
          <PharmacyMarker
            key={ph.pharmacyId}
            id={ph.apbNumber}
            marker={ph}
            icon={icon}
            onSelectionClick={this.selectPharmacy}
          />
        );
      });
    // console.log("renderPharmacyMarkers_END"+new Date())
    return result;
  };

  fitBoundsDay = (map: google.maps.Map) => {
    if (this.state.initialZoomCalculatedDay || this.props.pharmacyList.length === 0) return;
    // @ts-ignore
    const bounds = new window.google.maps.LatLngBounds();
    this.props.pharmacyList.forEach((d) => {
      if (d.latitude && d.longitude) {
        // @ts-ignore
        bounds.extend(new window.google.maps.LatLng(d.latitude, d.longitude));
      }
    });
    map.fitBounds(bounds);
    this.setState({ initialZoomCalculatedDay: true });
  };

  fitBoundsNight = (map: google.maps.Map) => {
    if (this.state.initialZoomCalculatedNight || this.props.pharmacyList.length === 0) return;
    // @ts-ignore
    const bounds = new window.google.maps.LatLngBounds();
    this.props.pharmacyList.forEach((d) => {
      if (d.latitude && d.longitude) {
        // @ts-ignore
        bounds.extend(new window.google.maps.LatLng(d.latitude, d.longitude));
      }
    });
    map.fitBounds(bounds);
    this.setState({ initialZoomCalculatedNight: true });
  };

  dateSearchChange = (date) => {
    this.setState({ searchDate: date });
    this.searchDuties({ date: moment(date).format(), notAssigned: false, planningId: this.state.searchPlanning });
  };

  renderCircleForPharmacy = (pharmacy, type) => {
    return type === 'DAY'
      ? [
          <Circle
            key={pharmacy.pharmacyId + 'max1'}
            center={{ lat: pharmacy.latitude, lng: pharmacy.longitude }}
            radius={pharmacy.minDistance}
            options={{
              fillColor: '#106BA3',
              strokeWeight: 0.5,
            }}
          />,
          // <Circle
          //   key={pharmacy.pharmacyId + 'max2'}
          //   center={{ lat: pharmacy.latitude, lng: pharmacy.longitude }}
          //   radius={pharmacy.maxDistance}
          //   options={{
          //     fillColor: '#106BA3',
          //     strokeWeight: 0.5,
          //   }}
          // />,
        ]
      : [
          <Circle
            key={pharmacy.pharmacyId + 'max1'}
            center={{ lat: pharmacy.latitude, lng: pharmacy.longitude }}
            radius={pharmacy.minDistance2}
            options={{
              fillColor: '#106BA3',
              strokeWeight: 0.5,
            }}
          />,
          // <Circle
          //   key={pharmacy.pharmacyId + 'max2'}
          //   center={{ lat: pharmacy.latitude, lng: pharmacy.longitude }}
          //   radius={pharmacy.maxDistance2}
          //   options={{
          //     fillColor: '#106BA3',
          //     strokeWeight: 0.5,
          //   }}
          // />,
        ];
  };

  renderAnchors = () => {
    const markers: any[] = [];
    if (this.state.showAnchor) {
      this.props.anchors.map((elem) => {
        // markers.push(<AnchorMarker marker={elem} icon={'http://maps.google.com/mapfiles/kml/pal5/icon13.png'} />);
        markers.push(
          <Circle
            key={elem.anchorId + 'Circle'}
            center={{ lat: Number(elem.latitude), lng: Number(elem.longitude) }}
            radius={elem.radius}
            options={{
              fillColor: '#10a259',
              strokeWeight: 0.5,
            }}
          />,
        );
      });
    }
    return markers;
  };

  renderMarker = (duties, type) => {
    const { pharmacySelected } = this.state;

    const pharmacies = this.renderPharmacyMarkers(duties, type);
    const dutiesMarker = this.renderPharmacyDutiesMarkers(duties, type);
    const pharmacyCircle = pharmacySelected ? this.renderCircleForPharmacy(pharmacySelected, type) : [];
    const anchors = this.renderAnchors();
    return [...pharmacies, ...dutiesMarker, ...pharmacyCircle, ...anchors];
  };

  onPharmacyChange = async (pharmacy: Instance<typeof Pharmacy>) => {
    if (pharmacy.pharmacyId != null) {
      const details = await this.props.getPharmacyDetails(pharmacy.pharmacyId);
      this._isMounted && this.setState({ pharmacySelected: details });
    } else {
      this._isMounted && this.setState({ pharmacySelected: undefined });
    }
  };

  render() {
    const {
      loading,
      requestingUserLocation,
      dutiesDay,
      dutiesNight,
      searchDate,
      searchPlanning,
      pharmacySelected,
      initialZoomCalculatedDay,
      initialZoomCalculatedNight,
    } = this.state;
    const { translate, pharmacyList, getPharmacyById, onDateChange, isSecretariat } = this.props;
    const centerCoordinateDay = {
      lat: 50.417531,
      lng: 3.908235,
    }; //this.findCenterCoordinate('DAY');
    const centerCoordinateNight = {
      lat: 50.417531,
      lng: 3.908235,
    }; //this.findCenterCoordinate('NIGHT');
    const centerCoordinates = {
      lat: 50.417531,
      lng: 3.908235,
    };
    const filteredDayDutyCount = dutiesDay.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level > 0,
    ).length;
    const filteredNightDutyCount = dutiesNight.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level > 0,
    ).length;

    return loading || requestingUserLocation ? (
      <Spinner />
    ) : (
      <Container>
        <SearchContainer>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Label style={{ marginRight: '10px' }}>{translate(TLCode.DUTYASSIGNMENT_Date)}</Label>
            <Button
              icon={IconNames.CHEVRON_LEFT}
              small={true}
              style={{ height: '30px', marginRight: '5px' }}
              onClick={() => {
                const date = moment(this.state.searchDate).subtract(1, 'd');
                this.setState({ searchDate: date.toDate() });
                this.searchDuties({ date: date.format(), notAssigned: false, planningId: searchPlanning });
                onDateChange(date.format());
              }}
            />
            <DateInput
              value={searchDate}
              canClearSelection={false}
              onChange={(value) => {
                this.dateSearchChange(value);
                onDateChange(moment(value).format());
              }}
            />
            <Button
              icon={IconNames.CHEVRON_RIGHT}
              small={true}
              style={{ height: '30px', marginLeft: '5px' }}
              onClick={() => {
                const date = moment(this.state.searchDate).add(1, 'd');
                this.setState({ searchDate: date.toDate() });
                this.searchDuties({ date: date.format(), notAssigned: false, planningId: searchPlanning });
                onDateChange(date.format());
              }}
            />
            <Button
              small={true}
              style={{ height: '30px', marginLeft: '5px' }}
              onClick={() => {
                const date = moment(this.state.searchDate).add(1, 'y');
                date.date(1);
                date.month(0);
                this.setState({ searchDate: date.toDate() });
                this.searchDuties({ date: date.format(), notAssigned: false, planningId: searchPlanning });
              }}
              text={translate(TLCode.GENERAL_NextYear)}
            />
            {isSecretariat && (
              <>
                <Checkbox
                  onChange={() => this.setState({ showPharmacyNotInDuty: !this.state.showPharmacyNotInDuty })}
                  checked={this.state.showPharmacyNotInDuty}
                  style={{ marginLeft: '5px', marginRight: '10px' }}
                >
                  {translate(TLCode.DUTIES_ShowOtherPharmacies)}
                </Checkbox>
                <Checkbox
                  onChange={() => this.setState({ showPharmacyDutyCircle: !this.state.showPharmacyDutyCircle })}
                  checked={this.state.showPharmacyDutyCircle}
                  style={{ marginLeft: '5px', marginRight: '10px' }}
                >
                  {translate(TLCode.DUTIES_ShowPharmaciesDutyCircle)}
                </Checkbox>
                <Checkbox
                  onChange={() => this.setState({ showPharmacyDuty30kmCircle: !this.state.showPharmacyDuty30kmCircle })}
                  checked={this.state.showPharmacyDuty30kmCircle}
                  style={{ marginLeft: '5px', marginRight: '10px' }}
                >
                  {translate(TLCode.DUTIES_ShowPharmaciesDuty30KmCircle)}
                </Checkbox>
                <Checkbox
                  onChange={() => this.setState({ showAnchor: !this.state.showAnchor })}
                  checked={this.state.showAnchor}
                  style={{ marginLeft: '5px', marginRight: '10px' }}
                >
                  {translate(TLCode.DUTIES_ShowAnchors)}
                </Checkbox>
                <PharmacySelection
                  pharmacyList={pharmacyList}
                  pharmacySelected={pharmacySelected}
                  getPharmacyById={getPharmacyById}
                  onChange={this.onPharmacyChange}
                  showDetails={false}
                  allowEmpty={true}
                />
              </>
            )}
          </div>
        </SearchContainer>
        {pharmacySelected && (
          <b>
            ({pharmacySelected!.level})&nbsp;&nbsp;{pharmacySelected!.apbNumber}&nbsp;-&nbsp;{pharmacySelected!.name}
            &nbsp;-&nbsp;{pharmacySelected!.address},&nbsp;
            {pharmacySelected!.zipCode}&nbsp;
            {pharmacySelected!.city}
          </b>
        )}
        <Maps>
          <MapContainer>
            <Label
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontWeight: 'bold',
              }}
            >
              {translate(TLCode.DUTYASSIGNMENT_DAY)}{' '}
              {isSecretariat && (
                <>
                  : {filteredDayDutyCount} {translate(TLCode.DUTIES_Duties)}
                </>
              )}
            </Label>
            <DivFlex>
              <PharmagardeMap center={centerCoordinateDay} onLoad={this.fitBoundsDay} gridSize={5}>
                {initialZoomCalculatedDay ? this.renderMarker(dutiesDay, 'DAY') : undefined}
              </PharmagardeMap>
            </DivFlex>
          </MapContainer>
          <MapContainer>
            <Label
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontWeight: 'bold',
              }}
            >
              {translate(TLCode.DUTYASSIGNMENT_NIGHT)}{' '}
              {isSecretariat && (
                <>
                  : {filteredNightDutyCount} {translate(TLCode.DUTIES_Duties)}
                </>
              )}
            </Label>
            <DivFlex>
              <PharmagardeMap center={centerCoordinateNight} onLoad={this.fitBoundsNight} gridSize={5}>
                {initialZoomCalculatedNight ? this.renderMarker(dutiesNight, 'NIGHT') : undefined}
              </PharmagardeMap>
            </DivFlex>
          </MapContainer>
        </Maps>
      </Container>
    );
  }
}

export default withTranslate(ShowDutiesOnMap);
